import { api } from '@/lib/api';
import { withEmbedPlayerSearchParams } from '@/lib/lyrraEduflowEmbedSnippet';

function unwrapApiRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const inner = r.data !== undefined ? r.data : raw;
  if (!inner || typeof inner !== 'object') return null;
  return inner as Record<string, unknown>;
}

export type OnboardingHelpKind =
  | 'dashboard'
  | 'navigation'
  | 'profile'
  | 'roles'
  | 'blocks'
  | 'designer-overview'
  | 'branches'
  | 'validation'
  | 'objectives'
  | 'ai-designer'
  | 'publication-status'
  | 'publication-links'
  | 'catalog-visibility'
  | 'enrollments'
  | 'groups-cohorts'
  | 'progress'
  | 'certificates'
  | 'media-library';

/**
 * ID du flow seed centre d’aide (`kind=dashboard` : tableau de bord, `kind=navigation` : menu latéral).
 * D’abord `VITE_ONBOARDING_*` ; sinon `GET /eduflows/onboarding-help-flow-id` (sans auth, aligné sur le player public).
 */
export async function fetchOnboardingHelpFlowId(kind: OnboardingHelpKind): Promise<string | null> {
  const fromEnv = (() => {
    if (kind === 'navigation') return (import.meta.env.VITE_ONBOARDING_NAVIGATION_FLOW_ID as string | undefined)?.trim();
    if (kind === 'profile') return (import.meta.env.VITE_ONBOARDING_PROFILE_FLOW_ID as string | undefined)?.trim();
    if (kind === 'roles') return (import.meta.env.VITE_ONBOARDING_ROLES_FLOW_ID as string | undefined)?.trim();
    if (kind === 'blocks') return (import.meta.env.VITE_ONBOARDING_BLOCK_TYPES_FLOW_ID as string | undefined)?.trim();
    if (kind === 'designer-overview')
      return (import.meta.env.VITE_ONBOARDING_DESIGNER_OVERVIEW_FLOW_ID as string | undefined)?.trim();
    if (kind === 'branches') return (import.meta.env.VITE_ONBOARDING_BRANCHES_FLOW_ID as string | undefined)?.trim();
    if (kind === 'validation')
      return (import.meta.env.VITE_ONBOARDING_VALIDATION_FLOW_ID as string | undefined)?.trim();
    if (kind === 'objectives')
      return (import.meta.env.VITE_ONBOARDING_OBJECTIVES_FLOW_ID as string | undefined)?.trim();
    if (kind === 'ai-designer')
      return (import.meta.env.VITE_ONBOARDING_AI_DESIGNER_FLOW_ID as string | undefined)?.trim();
    if (kind === 'publication-status')
      return (import.meta.env.VITE_ONBOARDING_PUBLICATION_STATUS_FLOW_ID as string | undefined)?.trim();
    if (kind === 'publication-links')
      return (import.meta.env.VITE_ONBOARDING_PUBLICATION_LINKS_FLOW_ID as string | undefined)?.trim();
    if (kind === 'catalog-visibility')
      return (import.meta.env.VITE_ONBOARDING_CATALOG_VISIBILITY_FLOW_ID as string | undefined)?.trim();
    if (kind === 'enrollments')
      return (import.meta.env.VITE_ONBOARDING_ENROLLMENTS_FLOW_ID as string | undefined)?.trim();
    if (kind === 'groups-cohorts')
      return (import.meta.env.VITE_ONBOARDING_GROUPS_COHORTS_FLOW_ID as string | undefined)?.trim();
    if (kind === 'progress')
      return (import.meta.env.VITE_ONBOARDING_PROGRESS_FLOW_ID as string | undefined)?.trim();
    if (kind === 'certificates')
      return (import.meta.env.VITE_ONBOARDING_CERTIFICATES_FLOW_ID as string | undefined)?.trim();
    if (kind === 'media-library')
      return (import.meta.env.VITE_ONBOARDING_MEDIA_LIBRARY_FLOW_ID as string | undefined)?.trim();
    return (import.meta.env.VITE_ONBOARDING_DASHBOARD_FLOW_ID as string | undefined)?.trim();
  })();
  if (fromEnv) return fromEnv;
  try {
    const params =
      kind === 'navigation'
        ? { kind: 'navigation' as const }
        : kind === 'profile'
          ? { kind: 'profile' as const }
          : kind === 'roles'
            ? { kind: 'roles' as const }
            : kind === 'blocks'
              ? { kind: 'blocks' as const }
              : kind === 'designer-overview'
                ? { kind: 'designer-overview' as const }
                : kind === 'branches'
                  ? { kind: 'branches' as const }
                  : kind === 'validation'
                    ? { kind: 'validation' as const }
                    : kind === 'objectives'
                      ? { kind: 'objectives' as const }
                      : kind === 'ai-designer'
                        ? { kind: 'ai-designer' as const }
                        : kind === 'publication-status'
                          ? { kind: 'publication-status' as const }
                          : kind === 'publication-links'
                            ? { kind: 'publication-links' as const }
                            : kind === 'catalog-visibility'
                              ? { kind: 'catalog-visibility' as const }
                              : kind === 'enrollments'
                                ? { kind: 'enrollments' as const }
                                : kind === 'groups-cohorts'
                                  ? { kind: 'groups-cohorts' as const }
                                  : kind === 'progress'
                                    ? { kind: 'progress' as const }
                                    : kind === 'certificates'
                                      ? { kind: 'certificates' as const }
                                      : kind === 'media-library'
                                        ? { kind: 'media-library' as const }
                      : undefined;
    const raw = await api.get<unknown>('/eduflows/onboarding-help-flow-id', params);
    const data = unwrapApiRecord(raw);
    const id = data?.flowId;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/** @deprecated Utiliser {@link fetchOnboardingHelpFlowId}('dashboard'). */
export async function fetchOnboardingDashboardFlowId(): Promise<string | null> {
  return fetchOnboardingHelpFlowId('dashboard');
}

export function buildOnboardingHelpPublicPlayerUrl(flowId: string, uiLang?: string): string {
  const base = `${window.location.origin}/eduflow/flows/${flowId}/public`;
  return withEmbedPlayerSearchParams(base, uiLang ? { lang: uiLang } : undefined);
}

/** @deprecated Utiliser {@link buildOnboardingHelpPublicPlayerUrl}. */
export function buildOnboardingDashboardPublicPlayerUrl(flowId: string): string {
  return buildOnboardingHelpPublicPlayerUrl(flowId);
}
